import classNames from 'classnames/bind';
import styles from './Carousel.module.scss';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import AutoScroll from 'embla-carousel-auto-scroll';
import AutoHeight from 'embla-carousel-auto-height';
import { useCallback, useContext, useEffect } from 'react';
import { NetspendContext } from 'library/contexts/NetspendContext';
import { NextButton, PrevButton, usePrevNextButtons } from './PrevNextButtons/PrevNextButtons';
import { PaginationButton, usePaginationButton } from './PaginationButton/PaginationButton';

let cx = classNames.bind(styles);

export function Carousel({ children, autoplay = false, autoplaySettings, pagination = false, previousNextControls = false, id, className, slidesDisplayed, declareSlideSize, gutterSize, peakaboo, loop, autoHeight }) {
	const classNameArray = className?.split(' ') || [];
	const { carouselLotties } = useContext(NetspendContext);
	const slideSize = declareSlideSize ? `${100 / slidesDisplayed}%` : null;

	function handleLottieAnimations(emblaApi, slide, lottieMethod) {
		emblaApi
			.slideNodes()
			[slide].querySelectorAll('.ns_lottie-container')
			.forEach((lottieInstance) => {
				const lottieId = lottieInstance.dataset.lottieid;
				if (lottieMethod === 'play') {
					carouselLotties.current[lottieId]?.play();
				} else if (lottieMethod === 'stop') {
					carouselLotties.current[lottieId]?.stop();
				}
			});
	}

	const plugins = [];
	if (autoplay && autoplaySettings?.playStyle === 'slide') {
		plugins.push(Autoplay({ stopOnMouseEnter: true, stopOnInteraction: false, playOnInit: true, delay: autoplaySettings?.slideDuration * 1000 }));
	}
	if (autoplay && autoplaySettings?.playStyle === 'scroll') {
		plugins.push(AutoScroll({ stopOnMouseEnter: true, speed: autoplaySettings?.scrollSpeed, stopOnInteraction: false, stopOnMouseEnter: autoplaySettings?.pauseOnMouseEnter }));
	}
	if (autoHeight) {
		plugins.push(AutoHeight());
	}

	const [emblaRef, emblaApi] = useEmblaCarousel(
		{
			loop: loop ? true : false,
			skipSnaps: false,
			inViewThreshold: 0.7,
		},
		plugins
	);

	const onNavButtonClick = useCallback((emblaApi) => {
		const autoplay = emblaApi?.plugins()?.autoplay;
		if (!autoplay) return;

		const resetOrStop = autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop;

		resetOrStop();
	}, []);

	const { selectedIndex, scrollSnaps, onDotButtonClick } = usePaginationButton(emblaApi, onNavButtonClick);

	const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi, onNavButtonClick);

	const onButtonAutoplayClick = useCallback(
		(callback, index) => {
			const autoScroll = emblaApi?.plugins()?.autoScroll;
			if (!autoScroll) return;

			const resetOrStop = autoScroll.options.stopOnInteraction === false ? autoScroll.reset : autoScroll.stop;

			resetOrStop();
			index || index === 0 ? callback(index) : callback();
		},
		[emblaApi]
	);

	const onSettle = useCallback((emblaApi) => {
		emblaApi.slidesInView().forEach((slide) => {
			handleLottieAnimations(emblaApi, slide, 'play');
		});
	}, []);

	const onSlidesInView = useCallback((emblaApi) => {
		emblaApi.slidesNotInView().forEach((slide) => {
			handleLottieAnimations(emblaApi, slide, 'stop');
		});
	}, []);

	useEffect(() => {
		if (!emblaApi) return;
		emblaApi.on('settle', onSettle).on('slidesInView', onSlidesInView);

		return () => {
			emblaApi.destroy();
		};
	}, [emblaApi]);

	return (
		<div id={id} className={cx(['ns_carousel', 'carousel', ...classNameArray, pagination && 'with-pagination', previousNextControls && 'with-prev-next-controls', declareSlideSize ? 'sized-slides' : 'unsized-slides', slidesDisplayed === 1 && 'full-width', peakaboo && 'mobile-peakaboo', autoHeight && 'auto-height'])} style={gutterSize && { '--gutter-size-override': `${gutterSize}px` }}>
			<div className={cx(['slide-viewport'])} ref={emblaRef}>
				<div className={cx(['slide-container'])}>
					{children.map((slide, index) => (
						<div key={index} className={cx(['ns_slide', 'slide', index === selectedIndex && 'active', slidesDisplayed === 1 && 'full-width'])} style={slideSize && { '--slide-size': slideSize }}>
							{slide}
						</div>
					))}
				</div>
			</div>
			{previousNextControls && (
				<div className={cx(['ns_prev-next-control-container', 'prev-next-control-container'])}>
					<div className={cx(['prev-next-controls'])}>
						<PrevButton onClick={autoplaySettings?.playStyle === 'scroll' ? () => onButtonAutoplayClick(onPrevButtonClick) : onPrevButtonClick} disabled={prevBtnDisabled} />
						<NextButton onClick={autoplaySettings?.playStyle === 'scroll' ? () => onButtonAutoplayClick(onNextButtonClick) : onNextButtonClick} disabled={nextBtnDisabled} />
					</div>
				</div>
			)}
			{pagination && (
				<div className={cx(['pagination-container'])}>
					<div className={cx(['pagination'])}>
						{scrollSnaps.map((_, index) => (
							<PaginationButton key={index} index={index} className={cx([index === selectedIndex && 'active'])} onClick={autoplaySettings?.playStyle === 'scroll' ? () => onButtonAutoplayClick(onDotButtonClick, index) : () => onDotButtonClick(index)} />
						))}
					</div>
				</div>
			)}
		</div>
	);
}
